import {
  Car,
  Check,
  ClipboardList,
  Clock,
  FileCheck,
  FileText,
  Hourglass,
  PauseCircle,
  TestTube,
  Users,
  X,
} from "lucide-react"

export interface StageConfig {
  // canonical stage slug used in DB
  id: string
  // display label
  label: string
  // duplicate of id to satisfy existing KanbanBoard usage
  stage: string
  // visual props
  icon: React.ElementType
  color: string
  // whether stage advances automatically on completion (true) or requires manual move (false)
  autoProgress: boolean
  // optional props used by JobPostingForm
  required?: boolean
  meta?: Record<string, string | number | string[] | number[] | boolean>
  segments?: Array<{
    id: string
    label: string
    required?: boolean
    ageLimit?: number
    meta?: Record<string, string | number | string[] | number[] | boolean>
  }>
}

export const stagesConfig: StageConfig[] = [
  // Basic Details
  {
    id: "basic-details",
    label: "Basic Details",
    stage: "basic-details",
    icon: ClipboardList,
    color: "#8B5CF6",
    autoProgress: true,
    required: true,
    segments: [
      { id: "contact-details", label: "Contact Details", required: true },
      { id: "dob", label: "Date of Birth", required: true, ageLimit: 18 },
      { id: "rtw-eligibility", label: "RTW Eligibility" },
      {
        id: "driving-licence-eligibility",
        label: "Driving Licence Eligibility",
      },
      { id: "vehicle-information", label: "Vehicle Information" },
      { id: "availability", label: "Availability" },
      { id: "cv-upload", label: "CV Upload" },
      { id: "reference-driver-id", label: "Reference Driver ID" },
    ],
  },
  // Kanban-only state after basic-details (filtered out in JobPostingForm)
  {
    id: "pending",
    label: "Pending",
    stage: "pending",
    icon: Clock,
    color: "#6B7280",
    autoProgress: false,
  },
  // Driver Onroad Test
  {
    id: "driver-onroad-test",
    label: "Driver Onroad Test",
    stage: "driver-onroad-test",
    icon: Car,
    color: "#3B82F6",
    autoProgress: true,
    meta: { passMark: 70 },
    segments: [],
  },
  // Compliance (Documents)
  {
    id: "documents",
    label: "Compliance",
    stage: "documents",
    icon: FileCheck,
    color: "#10B981",
    autoProgress: true,
    segments: [
      { id: "dvla-check", label: "DVLA Check" },
      { id: "rtw-check", label: "RTW Check" },
      { id: "cpc-upload", label: "CPC Upload" },
      { id: "tacho-card-upload", label: "Tacho Card Upload" },
      { id: "d4-medical-report-upload", label: "D4 Medical Report Upload" },
      { id: "insurance-check", label: "Insurance Check" },
    ],
  },
  // Screening Call
  {
    id: "screening",
    label: "Screening Call",
    stage: "screening",
    icon: Hourglass,
    color: "#F97316",
    autoProgress: false,
    segments: [],
  },
  // Orientation
  {
    id: "orientation",
    label: "Orientation",
    stage: "orientation",
    icon: Hourglass,
    color: "#F97316",
    autoProgress: false,
    segments: [],
  },
  // Orientation Quiz
  {
    id: "orientation-quiz",
    label: "Orientation Quiz",
    stage: "orientation-quiz",
    icon: TestTube,
    color: "#F97316",
    autoProgress: true,
    meta: { passMark: 70 },
    segments: [],
  },
  // Personal Details
  {
    id: "personal-details",
    label: "Personal Details",
    stage: "personal-details",
    icon: Users,
    color: "#10B981",
    autoProgress: true,
    required: false,
    segments: [
      { id: "identity-confirmation", label: "Identity Confirmation", required: true },
      { id: "bank-details", label: "Bank Details" },
      { id: "next-of-kin", label: "Next of Kin" },
      {
        id: "tax-details",
        label: "Tax Details",
        meta: { taxTypes: [] },
      },
      { id: "address-details", label: "Address Details" },
      { id: "headshot", label: "Headshot" },
      { id: "vehicle-release-form", label: "Vehicle Release Form" },
    ],
  },
  // Trial
  {
    id: "trial",
    label: "Trial",
    stage: "trial",
    icon: Hourglass,
    color: "#F97316",
    autoProgress: false,
    segments: [],
  },
  // Contract
  {
    id: "contract",
    label: "Contract",
    stage: "contract",
    icon: FileText,
    color: "#10B981",
    autoProgress: true,
    meta: { contract: "", defaultSalary: 0 },
    segments: [],
  },
  // Vehicle Booking (slug fix)
  {
    id: "vehicle-collection",
    label: "Vehicle Booking",
    stage: "vehicle-collection",
    icon: Car,
    color: "#FFBE37",
    autoProgress: true,
    segments: [
      { id: "payment-info", label: "Payment Info" },
      { id: "vehicle-booking", label: "Vehicle Booking" },
    ],
  },
  // Kanban-only terminal states (filtered out in JobPostingForm)
  {
    id: "complete",
    label: "Onboarded",
    stage: "complete",
    icon: Check,
    color: "#22C55E",
    autoProgress: false,
  },
  {
    id: "rejected",
    label: "Rejected",
    stage: "rejected",
    icon: X,
    color: "#EF4444",
    autoProgress: false,
  },
  {
    id: "hold",
    label: "Hold",
    stage: "hold",
    icon: PauseCircle,
    color: "#F59E0B",
    autoProgress: false,
  },
]

export const allDocuments = [
  {
    id: "cv",
    displayName: "CV",
    segment: "cv-upload",
  },
  {
    id: "dvla-front",
    displayName: "DVLA License (Front)",
    segment: "dvla-check",
  },
  {
    id: "dvla-back",
    displayName: "DVLA License (Back)",
    segment: "dvla-check",
  },
  {
    id: "insurance-proof",
    displayName: "Insurance Proof",
    segment: "insurance-check",
  },
  {
    id: "rtw-document",
    displayName: "Right to Work Document",
    segment: "rtw-check",
  },
  {
    id: "bank-proof",
    displayName: "Bank Proof",
    segment: "bank-details",
  },
  {
    id: "headshot",
    displayName: "Headshot Photo",
    segment: "headshot",
  },
  {
    id: "vehicle-release-form",
    displayName: "Vehicle Release Form",
    segment: "vehicle-release-form",
  },
  // {
  //   id: "driver-contract",
  //   displayName: "Driver Contract"
  // },
  {
    id: "tacho-card-front",
    displayName: "Tacho Card (Front)",
    segment: "tacho-card-upload",
  },
  {
    id: "tacho-card-back",
    displayName: "Tacho Card (Back)",
    segment: "tacho-card-upload",
  },
  {
    id: "cpc-front",
    displayName: "CPC Card (Front)",
    segment: "cpc-upload",
  },
  {
    id: "cpc-back",
    displayName: "CPC Card (Back)",
    segment: "cpc-upload",
  },
  {
    id: "d4-medical",
    displayName: "D4 Medical Report",
    segment: "d4-medical-report-upload",
  },
]
